import styled from 'styled-components';

export const TableContainer = styled.div`
  background: var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: 0;
  overflow: hidden;
  margin: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  height: ${({ tableHeight }) => tableHeight ? `${tableHeight}px` : '100%'};
  min-height: 400px;
`;

export const TableWrapper = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--border-color) transparent;
`;

export const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0rem;
  padding: 0 1rem;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-navbar);
  flex-wrap: wrap;
`;

export const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-right: 2rem;
`;

export const FilterLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const FilterSelect = styled.select`
  background: var(--bg-input);
  border: 1px solid var(--border-input);
  border-radius: 6px;
  padding: 0.375rem 0.75rem;
  color: var(--text-primary);
  font-size: 0.75rem;
  cursor: pointer;
  min-width: 120px;
  transition: all 0.2s ease;
  appearance: none;
  padding-right: 2.5rem;

  &:focus {
    outline: none;
    border-color: var(--brand-blue);
    box-shadow: 0 0 0 2px rgba(135, 206, 250, 0.2);
  }

  option {
    background: var(--bg-panel);
    color: var(--text-primary);
  }
`;

export const FilterSelectWrapper = styled.div`
  position: relative;
  display: inline-block;

  svg {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-muted);
    width: 16px;
    height: 16px;
    pointer-events: none;
  }
`;

export const DateRangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const DateInput = styled.input`
  background: var(--bg-input);
  border: 1px solid var(--border-input);
  border-radius: 6px;
  padding: 0.375rem 0.75rem;
  color: var(--text-primary);
  font-size: 0.75rem;
  width: 130px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--brand-blue);
    box-shadow: 0 0 0 2px rgba(135, 206, 250, 0.2);
  }

  &::-webkit-calendar-picker-indicator {
    filter: ${props => props.theme === 'dark' ? 'invert(0.5)' : 'invert(0.2)'};
    cursor: pointer;
  }

  &::-webkit-datetime-edit {
    color: var(--text-primary);
  }

  &::-webkit-datetime-edit-fields-wrapper {
    color: var(--text-primary);
  }

  &::-webkit-datetime-edit-text {
    color: var(--text-secondary);
  }

  &::-webkit-datetime-edit-month-field,
  &::-webkit-datetime-edit-day-field,
  &::-webkit-datetime-edit-year-field {
    color: var(--text-primary);
  }
`;

export const ClearFiltersButton = styled.button`
  background: var(--bg-panel);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 0.375rem 0.75rem;
  color: var(--text-secondary);
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.2s ease;

  &:hover {
    color: var(--text-primary);
    background: var(--bg-hover);
  }
`;

export const ActiveFiltersCount = styled.span`
  background: var(--brand-blue);
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 600;
  margin-left: 0.5rem;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--bg-panel);
  min-width: 1200px;
`;

export const TableHead = styled.thead`
  background: var(--bg-navbar);
  border-bottom: 1px solid var(--border-color);
`;

export const TableBody = styled.tbody`
  tr:nth-child(even) {
    background: var(--bg-panel);
  }
  
  tr:nth-child(odd) {
    background: var(--bg-panel-sub);
  }
  
  tr:hover {
    background: var(--bg-input) !important;
    cursor: pointer;
  }
`;

export const Th = styled.th`
  padding: 0.5rem 0.75rem;
  text-align: left;
  font-weight: 600;
  color: var(--text-title);
  font-size: 0.73rem;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
  position: relative;
  min-width: ${props => props.minWidth || 'auto'};
  max-width: ${props => props.maxWidth || 'none'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: var(--bg-hover);
  }

  ${props => props.sortable && `
    cursor: pointer;
    &:hover {
      background: var(--bg-hover);
    }
  `}
`;

export const Td = styled.td`
  padding: 0.5rem 0.75rem;
  color: var(--text-primary);
  font-size: 0.73rem;
  border-bottom: 1px solid var(--border-color);
  min-width: ${props => props.minWidth || 'auto'};
  max-width: ${props => props.maxWidth || 'none'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background-color 0.2s ease;
`;

export const SortIcon = styled.span`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  color: var(--text-muted);
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  border-top: 1px solid var(--border-color);
  background: var(--bg-panel);
  flex-shrink: 0;
`;

export const PaginationInfo = styled.div`
  color: var(--text-secondary);
  font-size: 0.73rem;
`;

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid var(--border-color);
  background: var(--bg-panel);
  color: var(--text-primary);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.73rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--bg-hover);
    border-color: var(--text-muted);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PageSizeSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--border-color);
  background: var(--bg-panel);
  color: var(--text-primary);
  border-radius: 6px;
  font-size: 0.73rem;
  cursor: pointer;
  appearance: none;
  padding-right: 2.5rem !important;

  &:focus {
    outline: none;
    border-color: var(--brand-blue);
  }

  option {
    background: var(--bg-panel);
    color: var(--text-primary);
  }
`;

export const PageSizeSelectWrapper = styled.div`
  position: relative;
  display: inline-block;

  svg {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-muted);
    width: 16px;
    height: 16px;
    pointer-events: none;
  }
`; 